import { createFileRoute, Outlet, useNavigate } from "@tanstack/react-router";
import { useEffect } from "react";
import { FacilityProvider } from "@/lib/facility/facility";
import { orpc } from "@/lib/orpc";
import { PolicyProvider } from "@/lib/policy";
import type { Me } from "@alm/api-contracts";
import { getBoardDevice } from "./-components/board-api";

// Outside `_authenticated`: a paired tablet holds no session. Its device token names a
// `sarah_board_devices` row and facility, is re-proved per request, and yields NO policy subject.
// Reads cover one building; writes need a six-digit quick code.
export const Route = createFileRoute("/sarah/board")({
  ssr: false,
  beforeLoad: async ({ context }): Promise<{ me: Me | null }> => {
    // A device never reads the session: the installer's cookie stays in the browser and would
    // hand a shared wall screen a person to act as.
    if (getBoardDevice()) return { me: null };
    // Anonymous is a real answer: the welcome card, not a redirect.
    try {
      const me = await context.queryClient.ensureQueryData(orpc.auth.me.queryOptions());
      return { me };
    } catch {
      return { me: null };
    }
  },
  component: BoardRealm,
});

function BoardRealm() {
  const { me } = Route.useRouteContext();
  const navigate = useNavigate();
  // `_authenticated`'s temporary-password rule, repeated so this route is not a way around it.
  const mustChoose = Boolean(me?.user.mustResetPassword);
  // An effect, not a thrown redirect: a redirect thrown during hydration of a client-only tree is
  // not applied.
  useEffect(() => {
    if (mustChoose) void navigate({ to: "/set-new-password", replace: true });
  }, [mustChoose, navigate]);
  if (mustChoose) return null;
  // No `AppShell`: a public corridor screen has no way back into the app. No providers without a
  // session, since both describe a person.
  if (!me) return <Outlet />;
  return (
    <PolicyProvider me={me}>
      <FacilityProvider>
        <Outlet />
      </FacilityProvider>
    </PolicyProvider>
  );
}
